/**
 * הלוואות — REDESIGN §6, a first-class screen.
 *
 * Top to bottom, exactly as §6 orders it:
 *
 *   1. **Header figures** — `סך החוב` is the screen's ONE hero (§8); `תשלום חודשי`,
 *      `ריבית משוקללת` and `הריבית שתשלם השנה` step down to 22px, each with its comparison line.
 *   2. **Trend** — total debt as one line, with the monthly change beneath it in words
 *      ("יורד ב-₪X בחודש בקצב הנוכחי"). A schedule projection is drawn dashed and says so.
 *   3. **`הכי יקר קודם`** — the interest ranking, the default sort, with the sort switch beside it.
 *      Every row: lender mark, a human name, the rate as the primary figure, balance, monthly
 *      payment, end date and `ריבית שנתית ≈ ₪X`. A row expands to the interest structure.
 *   4. **Timeline** — the next five years with a marker per loan, and the sentence under it.
 *   5. `הכל · פרטי · עסקי` — the segmented control, driven by the account-assignment table. Card
 *      facilities (Cal/Max) are grouped as `קרדיט בכרטיס` and never mixed with bank loans.
 *
 * The screen is one hero and four plain sections; only an expandable row earns a bordered surface.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SummaryResponse } from '../../../api-types.ts';
import { fmtDate, fmtMoney, mask, monthName } from '../format.ts';
import { he, providerName } from '../i18n/he.ts';
import { CategoryIcon, EmptyState as KitEmptyState, ProviderMark } from '../../../../shared/ui/src/index.tsx';
import { Icon } from '../components/Icon.tsx';
import { HeroFigure } from '../components/insight.tsx';
import { Chip, ErrorBox, Mask, Money, Num, Segmented, Skeleton } from '../components/ui.tsx';
import { DebtLine } from '../charts/DebtLine.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { useQuery } from '../hooks/useQuery.ts';
import { cacheKey } from '../hooks/cache.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';
import { asOfOf } from '../insight.ts';
import {
  LOAN_SCOPES,
  LOAN_SORTS,
  RANK_GROUP_LIMIT,
  debtMonthlyChange,
  endTimeline,
  endingSoon,
  filterLoans,
  loanTotals,
  rankGroups,
  sortLoans,
  splitOpen,
  topRateRow,
  type LoanRow,
  type LoanScope,
  type LoanSort,
  type LoansPayload,
} from '../loans.ts';

const SCOPE_LABEL: Readonly<Record<LoanScope, string>> = {
  all: he.loans.scopeAll,
  business: he.loans.scopeBusiness,
  personal: he.loans.scopePersonal,
};

const SORT_LABEL: Readonly<Record<LoanSort, string>> = {
  rate: he.loans.sortRate,
  balance: he.loans.sortBalance,
  payment: he.loans.sortPayment,
  end: he.loans.sortEnd,
};

function isScope(value: string | undefined): value is LoanScope {
  return value !== undefined && (LOAN_SCOPES as readonly string[]).includes(value);
}

function isSort(value: string | undefined): value is LoanSort {
  return value !== undefined && (LOAN_SORTS as readonly string[]).includes(value);
}

export function LoansScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api } = useApp();
  const { route, navigate } = useRouter();
  const asOf = summary ? asOfOf(summary) : '';
  const loans = useQuery<LoansPayload>(asOf ? cacheKey('loans', { asOf }) : null, () => api.loans(asOf), {
    scopes: ['accounts', 'summary'],
    enabled: Boolean(asOf),
  });

  // `?scope=` and `?sort=` are real deep links, the way every other screen keeps its filters.
  const scope: LoanScope = isScope(route.query.scope) ? route.query.scope : 'business';
  const sort: LoanSort = isSort(route.query.sort) ? route.query.sort : 'rate';
  const setQuery = (next: { scope?: LoanScope; sort?: LoanSort }) =>
    navigate(buildPath('loans', null, { scope: next.scope ?? scope, sort: next.sort ?? sort }), { replace: true });

  const payload = loans.data;
  const view = useMemo(() => {
    if (!payload) return null;
    const scoped = filterLoans(payload.rows, scope);
    const { open, closed } = splitOpen(scoped);
    return {
      open: sortLoans(open, sort),
      closed: sortLoans(closed, 'balance'),
      totals: loanTotals(open, payload.asOf),
      timeline: endTimeline(open, payload.asOf, 5),
      ending: endingSoon(open, payload.asOf, 12),
      change: debtMonthlyChange(open),
    };
  }, [payload, scope, sort]);

  if (loans.error && !payload) {
    return <ErrorBox title={he.screenError.title} body={he.screenError.body(he.loans.title)} />;
  }
  if (!payload || !view) {
    return (
      <div className="loans">
        <Skeleton className="v" />
        <Skeleton className="l" />
        <div className="sk ch" style={{ height: '160px' }} />
      </div>
    );
  }

  const scopeControl = (
    <Segmented<LoanScope>
      radio
      label={he.loans.scope}
      value={scope}
      options={LOAN_SCOPES.map((key) => [key, SCOPE_LABEL[key]] as const)}
      onChange={(next) => setQuery({ scope: next })}
    />
  );

  if (payload.rows.length === 0) {
    return (
      <div className="loans">
        <div className="loans__controls">{scopeControl}</div>
        <KitEmptyState illustration="noResults" title={he.loans.noneTitle} body={he.loans.noneBody} />
      </div>
    );
  }

  const totals = view.totals;
  const trend = payload.trend;
  const changeSentence =
    view.change === null
      ? null
      : view.change < 0
        ? he.loans.trendFalling(fmtMoney(Math.abs(view.change)))
        : view.change > 0
          ? he.loans.trendRising(fmtMoney(view.change))
          : he.loans.trendFlat;

  return (
    <div className="loans">
      <div className="loans__controls">{scopeControl}</div>

      {/* 1 ─ the header figures; only `סך החוב` is at hero size (§8) */}
      <HeroFigure
        label={he.loans.totalDebt}
        value={<Money agorot={totals.balanceAgorot} />}
        inputs={<>{he.loans.totalDebtNote(view.open.length)}</>}
        chip={
          totals.nextEnd ? (
            <Chip tone="outline" icon="calendar">
              {he.loans.colEnd} <Num>{fmtDate(totals.nextEnd)}</Num>
            </Chip>
          ) : null
        }
      />

      <div className="loans__figures">
        <Figure
          label={he.loans.monthly}
          value={<Money agorot={totals.monthlyAgorot} />}
          note={totals.monthlyEstimated ? `${he.loans.monthlyNote} · ${he.loans.estimated}` : he.loans.monthlyNote}
        />
        <Figure
          label={he.loans.weightedRate}
          value={totals.weightedRatePct === null ? he.app.none : <Num>{`${totals.weightedRatePct}%`}</Num>}
          note={totals.ratedShare >= 0.999 ? he.loans.weightedRateNote : `${he.loans.weightedRateNote} · ${Math.round(totals.ratedShare * 100)}%`}
        />
        <Figure label={he.loans.yearInterest} value={<Money agorot={-totals.yearInterestAgorot} />} note={he.loans.yearInterestNote} />
      </div>

      {/* 2 ─ the trend */}
      <section className="fk-section">
        <div className="loans__head">
          <h2 className="fk-section-title">{he.loans.trend}</h2>
          <span className="fk-meta">{payload.trendProjected ? he.loans.trendProjected : he.loans.trendActual}</span>
        </div>
        {trend.length >= 2 ? (
          <>
            <DebtLine
              points={trend}
              summary={`${he.loans.trendAria}: ${trend.map((point) => `${monthName(point.month)} ${fmtMoney(point.debtAgorot)}`).join(', ')}`}
              table={{
                caption: he.loans.trendAria,
                head: ['חודש', he.loans.totalDebt],
                rows: trend.map((point) => [monthName(point.month), fmtMoney(point.debtAgorot)]),
              }}
              tip={(index) => {
                const point = trend[index]!;
                return {
                  title: monthName(point.month),
                  rows: [{ label: he.loans.totalDebt, value: fmtMoney(point.debtAgorot), color: 'var(--series-2)' }],
                };
              }}
            />
            {changeSentence ? <p className="fk-body">{changeSentence}</p> : null}
          </>
        ) : (
          <KitEmptyState illustration="firstRun" title={he.empty.noHistoryTitle} body={he.empty.noHistoryBody} variant="inline" />
        )}
      </section>

      {/* 3 ─ the ranking */}
      <section className="fk-section">
        <div className="loans__head">
          <div>
            <h2 className="fk-section-title">{sort === 'rate' ? he.loans.ranking : SORT_LABEL[sort]}</h2>
            <p className="fk-meta">{sort === 'rate' ? he.loans.rankingSub : he.loans.sub(view.open.length, SCOPE_LABEL[scope])}</p>
          </div>
          <Segmented<LoanSort>
            radio
            label={he.loans.sort}
            value={sort}
            options={LOAN_SORTS.map((key) => [key, SORT_LABEL[key]] as const)}
            onChange={(next) => setQuery({ sort: next })}
          />
        </div>
        {view.open.length === 0 ? (
          <KitEmptyState
            illustration="noResults"
            title={he.loans.emptyTitle}
            body={he.loans.emptyBody}
            variant="inline"
            action={
              <Link className="btn sm" to={buildPath('settings', 'connections')}>
                {he.loans.emptyAction}
              </Link>
            }
          />
        ) : (
          <LoanGroups rows={view.open} sort={sort} asOf={payload.asOf} />
        )}
        {payload.source === 'derived' ? <p className="fk-meta loans__derived">{he.loans.derivedNote}</p> : null}
      </section>

      {/* 4 ─ the end-date timeline */}
      <section className="fk-section">
        <h2 className="fk-section-title">{he.loans.timeline}</h2>
        <div className="tline" role="img" aria-label={`${he.loans.timelineAria}: ${view.timeline.marks.map((m) => `${m.row.name} ${fmtDate(m.row.endDate ?? '')}`).join(', ')}`}>
          <div className="tline__track">
            {view.timeline.marks.map((markPoint) => (
              <span
                key={markPoint.row.id}
                className={`tline__mark tline__mark--${markPoint.row.kind === 'card-credit' ? 'card' : 'bank'}`}
                style={{ insetInlineStart: `${markPoint.position * 100}%` }}
                title={`${markPoint.row.name} · ${fmtDate(markPoint.row.endDate ?? '')}`}
              />
            ))}
          </div>
          <div className="tline__years">
            {view.timeline.years.map((year) => (
              <span className="tline__year" key={year}>
                <Num>{year}</Num>
              </span>
            ))}
          </div>
        </div>
        <p className="fk-body">
          {view.ending.count > 0 && view.ending.withinMonths !== null
            ? he.loans.freeingUp(view.ending.withinMonths, view.ending.count, fmtMoney(view.ending.monthlyPaymentFreedAgorot))
            : he.loans.freeingUpNone}
        </p>
        {view.timeline.undated.length ? <p className="fk-meta">{he.loans.timelineUndated(view.timeline.undated.length)}</p> : null}
      </section>

      {/* the historical facilities — kept, never counted (ARCHITECTURE §8) */}
      {view.closed.length ? <ClosedGroup rows={view.closed} asOf={payload.asOf} /> : null}
    </div>
  );
}

function Figure({ label, value, note }: { label: string; value: ReactNode; note: string }) {
  return (
    <div className="fk-figure loans__figure">
      <span className="fk-meta">{label}</span>
      <span className="fk-figure__value">{value}</span>
      <span className="fk-figure__note">{note}</span>
    </div>
  );
}

/**
 * Bank loans and card credit are two lists, never one (§6, BRIEF §9.4) — but the GROUPS follow the
 * active sort, so `הכי יקר קודם` still leads with the most expensive debt instead of always
 * putting bank loans first. The single most expensive loan is named outright above them.
 */
function LoanGroups({ rows, sort, asOf }: { rows: LoanRow[]; sort: LoanSort; asOf: string }) {
  const groups = rankGroups(rows, sort);
  const top = topRateRow(rows);
  return (
    <>
      {top === null ? null : (
        <p className="loans__top">
          <Chip tone="warn">{he.loans.topRate}</Chip>{' '}
          <span>{he.loans.topRateOf(top.name, `${top.ratePct ?? 0}%`)}</span>{' '}
          <span className="fk-meta">
            <Money agorot={top.balanceAgorot} />
            {top.monthlyPaymentAgorot === null ? null : (
              <>
                {' · '}
                <Money agorot={top.monthlyPaymentAgorot} /> {he.loans.colPayment}
              </>
            )}
          </span>
        </p>
      )}
      {groups.map((group) => (
        <LoanList key={group.kind} title={group.kind === 'bank' ? he.loans.groupBank : he.loans.groupCard} rows={group.rows} asOf={asOf} />
      ))}
    </>
  );
}

function LoanList({ title, rows, asOf }: { title: string; rows: LoanRow[]; asOf: string }) {
  const [open, setOpen] = useState(false);
  const shown = open ? rows : rows.slice(0, RANK_GROUP_LIMIT);
  const hidden = rows.length - shown.length;
  return (
    <div className="loan-group">
      <h3 className="kicker loan-group__t">{title}</h3>
      <div className="loan-rows">
        {shown.map((row) => (
          <LoanRowView key={row.id} row={row} asOf={asOf} />
        ))}
      </div>
      {hidden > 0 || open ? (
        <button type="button" className="link-btn loan-group__more" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {open ? he.urgency.less : he.loans.more(hidden)}
          <Icon name="chevDown" className={`sm later__chev${open ? ' later__chev--open' : ''}`} />
        </button>
      ) : null}
    </div>
  );
}

/** One loan. The rate is the primary figure (§6); the row expands to the interest structure. */
function LoanRowView({ row, asOf }: { row: LoanRow; asOf: string }) {
  const [open, setOpen] = useState(false);
  const id = `loan-${row.id.replace(/[^A-Za-z0-9_-]/g, '')}`;
  return (
    <div className="loan-row">
      <button type="button" className="loan-row__head" aria-expanded={open} aria-controls={id} onClick={() => setOpen((value) => !value)}>
        <span className="loan-row__ic" aria-hidden="true">
          <ProviderMark providerId={row.providerId} size={28} />
        </span>
        <span className="loan-row__main">
          <span className="loan-row__name">{row.name}</span>
          <span className="loan-row__meta">
            {providerName(row.providerId)}
            {row.numberLast4 ? (
              <>
                {' · '}
                <Mask>{mask(row.numberLast4)}</Mask>
              </>
            ) : null}
            {row.endDate ? (
              <>
                {' · '}
                {he.loans.colEnd} <Num>{fmtDate(row.endDate)}</Num>
              </>
            ) : (
              <> · {he.loans.contractOpen}</>
            )}
          </span>
        </span>
        <span className="loan-row__rate">
          {row.ratePct === null ? he.app.none : <Num>{`${row.ratePct}%`}</Num>}
          <span className="loan-row__rate-l">{he.loans.colRate}</span>
        </span>
        <span className="loan-row__nums">
          <span className="loan-row__num">
            <Money agorot={row.balanceAgorot} />
            <span className="loan-row__num-l">{he.loans.colBalance}</span>
          </span>
          <span className="loan-row__num">
            {row.monthlyPaymentAgorot === null ? he.app.none : <Money agorot={row.monthlyPaymentAgorot} />}
            <span className="loan-row__num-l">
              {he.loans.colPayment}
              {row.monthlyEstimated ? ` · ${he.loans.estimated}` : ''}
            </span>
          </span>
        </span>
        <Icon name="chevDown" className={`sm loan-row__chev${open ? ' loan-row__chev--open' : ''}`} />
      </button>
      <div className="loan-row__body" id={id} hidden={!open}>
        <div className="loan-row__facts">
          <span className="fk-meta">{he.loans.interest}</span>
          {row.rate && row.rate.entries.length ? (
            <ul className="loan-row__tiers">
              {row.rate.entries.map((entry, index) => (
                <li key={index} className={entry.expired ? 'is-expired' : undefined}>
                  <CategoryIcon glyph="percent" size={14} />
                  {entry.kind === 'INDE' || entry.indexed ? he.loans.interestIndexed : he.loans.interestFixed}
                  {entry.ratePct === null ? null : (
                    <>
                      {' '}
                      <Num>{`${entry.ratePct}%`}</Num>
                    </>
                  )}
                  {entry.role === 'margin' ? <span className="note"> +</span> : null}
                  {entry.toDate ? (
                    <span className="note">
                      {' '}
                      <Num>{fmtDate(entry.toDate)}</Num>
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="fk-meta">{he.loans.interestUnknown}</p>
          )}
        </div>
        <div className="loan-row__facts">
          <span className="fk-meta">{he.loans.contract}</span>
          <p className="fk-body">
            {row.startDate && row.endDate ? (
              <Num>{he.loans.contractRange(fmtDate(row.startDate), fmtDate(row.endDate))}</Num>
            ) : (
              he.loans.contractOpen
            )}
          </p>
          {row.monthsLeft !== null && row.monthsLeft > 0 ? <p className="fk-meta">{he.loans.monthsLeft(row.monthsLeft)}</p> : null}
          {row.yearInterestAgorot === null ? null : (
            <p className="fk-meta">{he.loans.yearInterestRow(fmtMoney(row.yearInterestAgorot))}</p>
          )}
          <Link className="link-btn" to={buildPath('cashflow', null, { account: row.id })}>
            {he.loans.toTransactions} <Icon name="arrowLeft" className="sm" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function ClosedGroup({ rows, asOf }: { rows: LoanRow[]; asOf: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="fk-section later">
      <button type="button" className="later__toggle" aria-expanded={open} aria-controls="loans-closed" onClick={() => setOpen((value) => !value)}>
        <Icon name="clock" className="sm" />
        <span>{he.loans.closed(rows.length)}</span>
        <span className="later__cta">
          {open ? he.loans.closedHide : he.loans.closedShow}
          <Icon name="chevDown" className={`sm later__chev${open ? ' later__chev--open' : ''}`} />
        </span>
      </button>
      <div id="loans-closed" hidden={!open}>
        <div className="loan-rows">
          {rows.map((row) => (
            <LoanRowView key={row.id} row={row} asOf={asOf} />
          ))}
        </div>
      </div>
    </section>
  );
}
